import { AlbumFileType } from '@/models/albumFileType'
import { Slide } from 'yet-another-react-lightbox'
import HeyGame from '@/models/heyGame.ts'
import { AlbumFileComment } from '@/models/albumFileComment.ts'
import { AlbumFileLike } from '@/models/albumFileLike.ts'
import { HeyDrive, OpenedImageCaption } from '@/models/drive.ts'

export interface AlbumFile {
  id: string
  author: string
  author_display_name: string
  /** Original file (full resolution). */
  file: string
  file_type: AlbumFileType
  file_name: string
  content_type: string
  thumbnail: string | null
  /**
   * Web-optimized version of the file. May be `null` if not yet processed.
   * When the original file is already compatible and small enough,
   * `detail` equals `file`. Use `detail ?? file` as the preferred display source.
   */
  detail: string | null
  game: HeyGame | null
  is_demo?: boolean
  file_creation: string | null
  created_at: string
  video_duration_seconds?: number | null
  comments?: Array<AlbumFileComment>
  likes?: Array<AlbumFileLike>
  description?: string
  size: number
  height?: number | null
  width?: number | null
  detail_size?: number
  thumbnail_watermarked?: string | null
  thumbnail_size_watermarked?: number | null
  detail_watermarked?: string | null
  detail_size_watermarked?: number | null
  watermark?: string | null
  watermark_config_hash?: string
}

export type MySlide = Slide & {
  game: HeyGame | null
  drive: HeyDrive | null
}

/**
 * Date of a file as shown to users: the capture date when the file carries one,
 * otherwise the upload date.
 */
export function formatFileDateTime(file: AlbumFile): string {
  const date = new Date(file.file_creation || file.created_at)
  return `${date.toLocaleDateString()}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

/**
 * Caption below an opened (fullscreen) image, as configured on the drive.
 *
 * Returns `null` for the default (`''`) — callers then keep the per-drive-type default:
 * normal drives show "<uploader> <date>", prof drives show nothing (a photographer's
 * client must not see who uploaded a file). Any other value overrides that default
 * for both drive types.
 */
export function buildOpenedImageCaption(file: AlbumFile, caption: OpenedImageCaption | null | undefined): string | null {
  if (!caption) {
    return null
  }

  const parts = caption.split(',')
  const values: Array<string> = []
  if (parts.includes('filename')) {
    values.push(file.file_name)
  }
  if (parts.includes('date')) {
    values.push(formatFileDateTime(file))
  }

  return values.filter(Boolean).join(' · ') || null
}

function formOptionalDescription(file: AlbumFile) {
  if (!file.description) {
    return ''
  }

  return `${file.description}\n\n`
}

export function prepareForLightbox(file: AlbumFile, drive: HeyDrive | null): MySlide {
  if (file.file_type === AlbumFileType.IMAGE) {
    if (file.is_demo) {
      return {
        src: file.detail ? file.detail : file.file,
        share: file.detail ? file.detail : file.file,
        description: 'Demo',
        download: file.file,
        game: file.game,
        drive,
      }
    }

    return {
      src: file.detail ? file.detail : file.file,
      share: file.detail ? file.detail : file.file,
      download: `${import.meta.env.VITE_PUBLIC_API_URL}/api/files/${file.id}/download/`,
      description: `${formOptionalDescription(file)}${new Date(file.file_creation || file.created_at).toLocaleString()} - ${file.author_display_name}`,
      game: file.game,
      drive,
    }
  }

  if (file.is_demo) {
    return {
      type: 'video',
      sources: [
        {
          src: file.detail ? file.detail : file.file,
          type: file.content_type,
        },
      ],
      share: file.detail ? file.detail : file.file,
      download: file.file,
      description: `Demo`,
      game: file.game,
      drive,
    }
  }

  return {
    type: 'video',
    sources: [
      {
        src: file.file,
        type: file.content_type,
      },
      ...(file.detail ?[{
        src: file.detail,
        type: 'video/mp4',
      }] : [])
    ],
    // TODO: Check if this works on large videos
    share: file.detail ? file.detail : file.file,
    download: `${import.meta.env.VITE_PUBLIC_API_URL}/api/files/${file.id}/download/`,
    description: `${formOptionalDescription(file)}${new Date(file.file_creation || file.created_at).toLocaleString()} - ${file.author_display_name}`,
    game: file.game,
    drive,
  }
}
